import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { StarfieldCanvas } from "../components/StarfieldCanvas";
import { useLang } from "../store/lang";

const validateCircle = (pts) => {
  if (pts.length < 30) return false;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const w = Math.max(...xs) - Math.min(...xs);
  const h = Math.max(...ys) - Math.min(...ys);
  if (w < 60 || h < 60) return false;
  const ratio = w / h;
  if (ratio < 0.55 || ratio > 1.8) return false;

  const first = pts[0];
  const last = pts[pts.length - 1];
  if (Math.hypot(first.x - last.x, first.y - last.y) / ((w + h) / 2) > 0.45) return false;

  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  let angle = 0;
  for (let i = 1; i < pts.length; i++) {
    const a1 = Math.atan2(pts[i - 1].y - cy, pts[i - 1].x - cx);
    const a2 = Math.atan2(pts[i].y - cy, pts[i].x - cx);
    let d = a2 - a1;
    if (d > Math.PI) d -= 2 * Math.PI;
    if (d < -Math.PI) d += 2 * Math.PI;
    angle += d;
  }
  if (Math.abs(angle) < 5.2) return false;

  const radii = pts.map((p) => Math.hypot(p.x - cx, p.y - cy));
  const mean = radii.reduce((a, b) => a + b, 0) / radii.length;
  const sd = Math.sqrt(radii.reduce((a, b) => a + (b - mean) ** 2, 0) / radii.length);
  return sd / mean < 0.5;
};

export const DrawGate = ({ onEnter }) => {
  const { ur } = useLang();
  const canvasRef = useRef(null);
  const ptsRef = useRef([]);
  const drawingRef = useRef(false);
  const [fail, setFail] = useState(0);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const c = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const redraw = () => {
      c.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      const pts = ptsRef.current;
      if (pts.length < 2) return;
      c.beginPath();
      c.moveTo(pts[0].x, pts[0].y);
      for (const p of pts) c.lineTo(p.x, p.y);
      c.strokeStyle = "#F3E5AB";
      c.lineWidth = 3;
      c.lineCap = "round";
      c.lineJoin = "round";
      c.shadowColor = "rgba(243,229,171,0.9)";
      c.shadowBlur = 16;
      c.stroke();
      c.shadowBlur = 0;
    };

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e) => {
      drawingRef.current = true;
      ptsRef.current = [pos(e)];
    };
    const move = (e) => {
      if (!drawingRef.current) return;
      ptsRef.current.push(pos(e));
      redraw();
    };
    const up = () => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      if (validateCircle(ptsRef.current)) {
        setSuccess(true);
        setTimeout(onEnter, 1000);
      } else if (ptsRef.current.length > 4) {
        setFail((f) => f + 1);
        ptsRef.current = [];
        redraw();
      } else {
        ptsRef.current = [];
        redraw();
      }
    };

    canvas.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [onEnter]);

  return (
    <motion.div
      exit={{ opacity: 0, transition: { duration: 0.9 } }}
      className="absolute inset-0 z-40 bg-[#070814]"
      data-testid="draw-gate"
    >
      <StarfieldCanvas className="absolute inset-0 w-full h-full opacity-60" />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-52 h-52 sm:w-64 sm:h-64 rounded-full border border-dashed border-amber-200/20" />
      </div>

      <div className="absolute top-0 inset-x-0 pt-16 sm:pt-20 text-center px-6 pointer-events-none">
        <p className="eyebrow mb-4">{ur ? "کہانی کی کتاب بند ہے" : "the storybook is sealed"}</p>
        <h1 className={`text-2xl sm:text-4xl gold-text ${ur ? "font-urdu leading-[2]" : "font-royal"}`} dir={ur ? "rtl" : "ltr"}>
          {ur ? "چاندنی کا دائرہ بنائیں" : "Trace a Circle of Moonlight"}
        </h1>
        <p className={`mt-4 text-sm text-slate-400 font-light ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
          {ur
            ? "ہایا کی کہانی کھولنے کے لیے انگلی یا ماؤس سے ایک مکمل چمکتا دائرہ بنائیں"
            : "draw one full glowing ring with your finger or mouse to open Haya's fairytale"}
        </p>
      </div>

      <canvas
        ref={canvasRef}
        data-testid="draw-gate-canvas"
        className="absolute inset-0 w-full h-full cursor-crosshair"
        style={{ touchAction: "none" }}
      />

      <div className="absolute bottom-0 inset-x-0 pb-10 text-center px-6">
        {success ? (
          <motion.p
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="font-script text-3xl text-rose-200"
            data-testid="draw-gate-success"
          >
            {ur ? "کتاب کھل رہی ہے…" : "the storybook opens…"}
          </motion.p>
        ) : fail > 0 ? (
          <motion.p
            key={fail}
            initial={{ x: -8 }}
            animate={{ x: [0, -8, 8, -4, 0] }}
            transition={{ duration: 0.4 }}
            className="text-sm text-rose-300/90"
            data-testid="draw-gate-fail"
          >
            {ur ? "قریب تھے — ایک مکمل دائرہ بنائیں" : "almost — trace one complete circle"}
          </motion.p>
        ) : null}
        <button
          data-testid="draw-gate-skip"
          onClick={onEnter}
          className="mt-4 text-[0.62rem] tracking-[0.3em] uppercase text-slate-500 hover:text-amber-200 transition-colors duration-300"
        >
          {ur ? "یا صرف چھو کر داخل ہوں" : "or simply tap to enter"}
        </button>
      </div>
    </motion.div>
  );
};
